import { Component, EventEmitter, Input, Output, ViewChild, forwardRef, Injector, OnInit, ChangeDetectorRef } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor, Validator, AbstractControl, ValidationErrors, NgControl, NG_ASYNC_VALIDATORS, AsyncValidator } from '@angular/forms';
import { MonacoEditorComponent, MonacoEditorConstructionOptions, MonacoEditorLoaderService, MonacoStandaloneCodeEditor } from '@materia-ui/ngx-monaco-editor';
import { filter, take } from 'rxjs/operators';
import { MonacoConfigService } from '../../services/monaco-editor/monaco-config-service';

@Component({
    selector: 'json-editor',
    templateUrl: './json-editor.component.html',
    styleUrls: ['./json-editor.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => JsonEditorComponent),
            multi: true,
        },
        {
            provide: NG_ASYNC_VALIDATORS,
            useExisting: forwardRef(() => JsonEditorComponent),
            multi: true,
        },
    ],
    standalone: false
})
export class JsonEditorComponent implements ControlValueAccessor, Validator, AsyncValidator, OnInit {
  @Input() validationSchema?: any;
  private _value: string = '{}';
  @Input() readOnly: boolean = false;

  @Output() jsonValidationStatus: EventEmitter<boolean> = new EventEmitter<boolean>();

  private static instanceCounter = 0;
  private instanceId: number;
  private editor: MonacoStandaloneCodeEditor;
  private markerValidationDelay = 1250;

  validationErrors: string[] = [];
  isValid: boolean = true;
  isTouched: boolean = false;
  
  public ngControl: NgControl | null;

  get value(): any {
    return this._value;
  }

  @Input()
  set value(value: any) {
    if (value !== this._value) {
      this._value = value;
      this.onChange(value);
      this.onTouched();
    }
  }

  onChange: any = () => { };
  onTouched: any = () => { };
  setTouched(): void {
    this.isTouched = true;
  }

  @ViewChild(MonacoEditorComponent, { static: false })
  monacoComponent: MonacoEditorComponent;
  editorOptions: MonacoEditorConstructionOptions = {
    theme: 'appTheme',
    language: 'json',
    roundedSelection: true,
    autoIndent: 'full'
  };

  modelUri: monaco.Uri;

  constructor(
    private readonly monacoLoaderService: MonacoEditorLoaderService,
    private readonly monacoConfigService: MonacoConfigService,
    private readonly injector: Injector,
    private cdRef: ChangeDetectorRef
  ) {
    this.instanceId = JsonEditorComponent.instanceCounter++;
  }

  ngOnInit(): void {
    // Get the NgControl reference using the Injector
    this.ngControl = this.injector.get(NgControl, null);
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  editorInit(editor: MonacoStandaloneCodeEditor) {
    this.editor = editor;
    this.modelUri = monaco.Uri.parse(`a://b/${this.instanceId}.json`);
  
    this.monacoLoaderService.isMonacoLoaded$
      .pipe(
        filter(isLoaded => !!isLoaded),
        take(1)
      )
      .subscribe(() => {
        // Beautify the initial JSON value before creating the model.
        let beautifiedValue = this._value;
        try {
          // Try to parse and re-stringify with indentation
          const parsed = JSON.parse(this._value);
          beautifiedValue = JSON.stringify(parsed, null, 2);
        } catch (e) {
          console.log('Failed to parse JSON', e);
          // If parsing fails, leave beautifiedValue as is.
        }
        
        // Create the Monaco model using the beautified JSON.
        editor.setModel(monaco.editor.createModel(beautifiedValue, 'json', this.modelUri));
  
        // If beautification changed the value, update our internal value
        // and notify Angular.
        if (beautifiedValue !== this._value) {
          this._value = beautifiedValue;
          this.onChange(beautifiedValue);
        }
  
        if (this.validationSchema) {
          this.registerMonacoJsonSchemaValidator();
        }
        monaco.editor.setTheme('appTheme');
        editor.updateOptions({ readOnly: this.readOnly });
  
        // (Optional) Programmatic content selection example.
        editor.setSelection({
          startLineNumber: 1,
          startColumn: 1,
          endColumn: 50,
          endLineNumber: 3
        });
  
        // Set the current value (mark as touched).
        this.onEditorValueChange(editor.getValue());
  
        editor.onDidChangeModelContent(() => {
          this.setTouched();
          setTimeout(() => this.onEditorValueChange(editor.getValue()));
        });
      });
  }  

  registerMonacoJsonSchemaValidator() {
    this.monacoConfigService.addSchema({
      // Make the URI unique per instance
      uri: `mySchema-${this.instanceId}`,
      // Apply this schema to the current model only
      fileMatch: [this.modelUri.toString()],
      schema: this.validationSchema
    });
  }

  // called whenever reactive form control value is changed from outside
  writeValue(value: any): void {
    if (value == null || value === '') {
      value = '{}';
    }
    try {
      const parsed = JSON.parse(value);
      value = JSON.stringify(parsed, null, 2);
    } catch (e) {
      // If not valid JSON, leave as-is.
    }
    this._value = value;
    if (this.editor) {
      this.editor.setValue(value);
    }
  }
  
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  validate(control: AbstractControl): Promise<ValidationErrors | null> | null {
    return new Promise((resolve) => {
      if (!this.editor) {
        return resolve(null); // Not ready yet
      }
  
      if (!this.validationSchema) {
        this.isValid = true;
        this.validationErrors = [];
        return resolve(null);
      }
  
      setTimeout(() => {
        const markers = monaco.editor.getModelMarkers({ resource: this.modelUri, owner: 'json' });
  
        this.validationErrors = markers.map(marker => {
          return `Line ${marker.startLineNumber}, Column ${marker.startColumn}: ${marker.message}`;
        });
  
        this.isValid = this.validationErrors.length === 0;
        resolve(this.isValid ? null : { invalidJson: true, errors: this.validationErrors });
      }, this.markerValidationDelay);
    });
  }
  
  setDisabledState(isDisabled: boolean): void {
    this.readOnly ||= isDisabled;
    
    // If your editor instance is already available, update its options.
    if (this.editor) {
      this.editor.updateOptions({ readOnly: isDisabled });
    }
  }

  onEditorValueChange(value: string): void {
    this.value = value;

    if (this.ngControl && this.ngControl.control && this.ngControl.control.value !== value) {
      this.ngControl.control.setValue(value, { emitEvent: false });
      // triggers validateControl() method
      this.ngControl.control.updateValueAndValidity();
    }

    // tell Angular monaco editor is updated, otherwise throws
    // ERROR RuntimeError: NG0100: ExpressionChangedAfterItHasBeenCheckedError: Expression has changed after it was checked
    this.cdRef.detectChanges();
  }

  beautify() {
    setTimeout(() => {
      try {
        let jsonValue = JSON.parse(this.value);
        let formattedValue = JSON.stringify(jsonValue, null, 2);
        this.editor.setValue(formattedValue);
      } catch (e) {
        // if not valid json we do not beautify
      }
    }, 0);
  }
}
